import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Users, Search, Filter, Shield, User, MoreVertical, Ban, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCommunity } from '../hooks/useCommunity';
import { cn } from '../utils';
import { api } from '../api';

const CommunityMembers = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { community, loading: communityLoading, isAdmin } = useCommunity(id);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const memberData = await api.get(`/users?community_id=${id}`);
        setMembers(Array.isArray(memberData) ? memberData : []);
      } catch (err) {
        console.error('Failed to fetch members', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [id]);

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(search.toLowerCase()) || 
    m.role?.toLowerCase().includes(search.toLowerCase())
  );

  if (communityLoading && !community) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
        <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
        <p className="font-bold uppercase tracking-widest text-[10px]">Loading Members...</p>
      </div>
    );
  }

  return (
    <div className="min-w-0 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">Community Members</h1>
              <p className="text-sm sm:text-base text-gray-500 font-medium">
                {community ? `${community.members_count?.toLocaleString() || 0} people building together in ${community.name}.` : 'Building together.'}
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-8">
             <div className="flex-1 bg-white rounded-3xl border border-gray-100 p-2 flex items-center shadow-sm focus-within:ring-2 focus-within:ring-brand-indigo/10 transition-all">
                <div className="p-3 text-gray-400">
                   <Search size={20} />
                </div>
                <input 
                  type="text" 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Find a member..."
                  className="flex-1 bg-transparent border-none outline-none font-medium py-2"
                />
             </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {loading ? (
              <div className="col-span-full py-20 text-center">
                <Loader2 size={32} className="animate-spin text-brand-indigo mx-auto mb-4" />
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest">Loading membership list...</p>
              </div>
            ) : filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <div key={member.id} className="bg-white p-6 rounded-[32px] border border-gray-100 flex flex-col items-center text-center group hover:shadow-2xl hover:shadow-gray-100 transition-all shadow-sm relative overflow-hidden">
                  <div className="w-20 h-20 rounded-[2rem] bg-gray-50 overflow-hidden mb-4 shadow-inner group-hover:scale-110 transition-transform duration-500">
                    <img 
                      src={member.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.name}`} 
                      alt={member.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1 truncate w-full">{member.name}</h3>
                  <div className="flex items-center gap-1.5 mb-4">
                    {member.role === 'admin' || member.role === 'moderator' ? (
                      <ShieldCheck size={12} className="text-brand-indigo" />
                    ) : (
                      <User size={12} className="text-gray-400" />
                    )}
                    <span className={cn(
                      "text-[9px] font-bold uppercase tracking-widest",
                      member.role === 'admin' || member.role === 'moderator' ? "text-brand-indigo" : "text-gray-400"
                    )}>
                      {member.role || 'Member'}
                    </span>
                  </div>
                  
                  {isAdmin && member.id !== currentUser?.id && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0">
                      <button 
                        className="p-2 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all"
                        title="Moderate Member"
                      >
                        <Shield size={16} />
                      </button>
                      <button 
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        title="Remove Member"
                      >
                        <Ban size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center bg-white rounded-[32px] border border-gray-50">
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest">No members found matching "{search}"</p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default CommunityMembers;
